import { selectIsInternalAdmin } from 'accessors'
import LoadingIndicator from 'components/ReusableComponents/LoadingIndicator'
import ServiceHistoryDialog from 'components/ReusableComponents/ServiceHIstoryDialog'
import { componentModes } from 'features/NewHotelDialog/constants'
import moment from 'moment'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import subsidiariesApi from 'services/api/subsidiaries'

import AddServiceDialog from '../../../components/ReusableComponents/AddServiceDialog/index'
import EditServiceDialog from '../../../components/ReusableComponents/EditServiceDialog/index'
import { USCurrency } from '../../../components/ReusableComponents/FormattedPrice/index'
import {
  directfulServiceNames,
  directfulServiceTypes
} from '../../../main/directfulServices'
import HotelPlanTooltip from 'components/ReusableComponents/HotelPlanTooltip'
import { useHotelPlanInfo } from 'features/Hotel/useHotelPlanInfo'

import {
  getSubscriptionRowDisplayName,
  getSubscriptionRowDisplayRate,
  shouldShowSubscriptionRow
} from '../hotelPlanDisplayName'
import Box from '../Box/index'
import EditPaymentPeriodDialog from '../PrepayPreferences/EditPaymentPeriodDialog'
import { ReactComponent as Chevron } from './chevron.svg'
import { ReactComponent as HistoryIcon } from './history.svg'
import { ReactComponent as PlusIcon } from './plus.svg'
import styles from './Services.module.scss'

const Services = ({ billingTaxRate, subscriptions, status, onSave }) => {
  const [showAddDialog, setShowAddDialog] = useState()
  const [editedSubscription, setEditedSubscription] = useState()
  const [showHistoryDialog, setShowHistoryDialog] = useState(false)
  const [showSwitchToPrepayDialog, setShowSwitchToPrepayDialog] = useState(false)
  const [nearestStartDate, setNearestStartDate] = useState()
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    subsidiariesApi.prepayPeriod
      .get()
      .then(res => {
        setNearestStartDate(res.nearestStartDate)
        setIsLoading(false)
      })
      .catch(() => {
        setIsLoading(false)
      })
  }, [])

  const canGetMoreServices =
    status === 'Paused'
      ? false
      : subscriptions.map(s => s.plan).length < Object.keys(directfulServiceNames).length

  const isInternalAdmin = useSelector(selectIsInternalAdmin)

  if (isLoading) {
    return <LoadingIndicator />
  }

  return (
    <>
      {showAddDialog && (
        <AddServiceDialog
          currentServices={subscriptions}
          handleClose={() => setShowAddDialog(false)}
          billingTaxRate={billingTaxRate}
        />
      )}
      {editedSubscription && (
        <EditServiceDialog
          handleClose={() => setEditedSubscription(null)}
          service={editedSubscription}
          billingTaxRate={billingTaxRate}
        />
      )}
      {showHistoryDialog && (
        <ServiceHistoryDialog
          onClose={() => setShowHistoryDialog(false)}
          subscriptions={subscriptions}
        />
      )}
      {showSwitchToPrepayDialog && (
        <EditPaymentPeriodDialog
          onClose={() => setShowSwitchToPrepayDialog(false)}
          nearestStartDate={nearestStartDate}
          mode={componentModes.switchToPrepay}
          onSave={onSave}
        />
      )}
      <Box className={styles.box}>
        <div className={styles.content}>
          <div className={styles.title}>
            <span>Services &amp; fees</span>
            {canGetMoreServices && isInternalAdmin && (
              <button onClick={() => setShowAddDialog(true)}>
                <PlusIcon />
              </button>
            )}
          </div>
          {Object.keys(directfulServiceTypes).map(serviceType => {
            const subscription = subscriptions.find(s => s.plan === serviceType)
            if (!subscription) {
              return null
            }
            if (!shouldShowSubscriptionRow(subscription, subscriptions)) {
              return null
            }
            return (
              <Row
                {...subscription}
                key={serviceType}
                subscriptions={subscriptions}
                handleClick={() => setEditedSubscription(subscription)}
                isClickable={status === 'Paused' ? false : isInternalAdmin}
              />
            )
          })}
          {subscriptions.length === 0 && (
            <div className={styles.errorText}>No active services</div>
          )}
        </div>
        <button
          className={styles.showHistoryButton}
          onClick={() => setShowHistoryDialog(true)}
        >
          <HistoryIcon />
          Show history
        </button>
        <button
          className={styles.showHistoryButton}
          onClick={() => setShowSwitchToPrepayDialog(true)}
        >
          <HistoryIcon />
          Switch to prepay
        </button>
      </Box>
    </>
  )
}

const Row = ({
  plan,
  planRate,
  isFlatMonthly = true,
  handleClick,
  isClickable,
  startDate,
  endDate,
  isActive,
  packageName,
  subscriptions = []
}) => {
  const { solutionFeatures, shouldShow: shouldShowPlanTooltip } = useHotelPlanInfo(subscriptions)
  const subscription = { plan, planRate, isFlatMonthly, startDate, endDate, packageName }
  const isCoreTierPlan =
    plan === 'DirectfulCore' ||
    plan === 'Unlimited' ||
    plan === 'Basic' ||
    plan === 'Messaging'
  const displayName = getSubscriptionRowDisplayName(subscription, subscriptions)
  const displayRate = getSubscriptionRowDisplayRate(subscription, subscriptions)
  const showPlanTooltip = isCoreTierPlan && shouldShowPlanTooltip

  return (
    <div
      onClick={isClickable ? handleClick : undefined}
      className={isClickable ? styles.rowClickable : styles.row}
    >
      <div>
        <span className={styles.planNameRow}>
          <span className={styles.planName}>{displayName}</span>
          {showPlanTooltip && (
            <HotelPlanTooltip
              solutionFeatures={solutionFeatures}
              iconOnly
              iconSize={16}
              align="left"
            />
          )}
        </span>
        <p className={styles.startDate}>
          {isActive ? (
            <>
              <span className={styles.active}>Active</span>
              &nbsp;since {moment(startDate).format('MMM D, YYYY')}
            </>
          ) : (
            <>
              <span className={styles.disabled}>Disabled</span> on{' '}
              {moment(endDate).format('MMM D, YYYY')}
            </>
          )}
        </p>
      </div>
      <span className={styles.planRate}>
        {isFlatMonthly ? <USCurrency>{displayRate}</USCurrency> : <>%{displayRate}</>}
      </span>
      <Chevron />
    </div>
  )
}

export default Services
